import {DynamicRef} from "../../dynamic-ref/dynamic-ref";
import {useCallback, useEffect, useMemo, useState} from "react";
import {elementFindViewportRoot, elementGetIndexInParent, elementHtmlCollectionToArray} from "@wix/devzai-utils-dom";
import {Edges, evaluateFunction, iterableMin, ObservableSet} from "@wix/devzai-utils-common";
import {useObservable} from "../use-observable/use-observable";
import {useDebouncedValue} from "../use-debounced-value";

export function useFirstVisibleChildInViewport<E extends Element> (
    elementRef: DynamicRef<E>,
    options: {
        frameShrinking?: Edges<number>;
        enabled?: boolean;
        threshold?: number
    } = {}
) {
    const {
        frameShrinking,
        enabled = true,
        threshold = 0.01
    } = options;

    const marginStr = frameShrinking ? `${-frameShrinking.top}px ${-frameShrinking.right}px ${-frameShrinking.bottom}px ${-frameShrinking.left}px` : undefined;

    const [targetElement, setTargetElement] = useState<E | null>(elementRef.current);
    const visibleElementsObservableSet = useMemo(() => new ObservableSet<Element>(), []);

    elementRef.onRefWasChanged(element => {
        setTargetElement(element);
    })

    const intersectionObserverElements = useMemo(() => {

        if (targetElement) {
            return {
                observerRoot: evaluateFunction(() => {

                    const viewportRoot = elementFindViewportRoot(targetElement);

                    return viewportRoot !== targetElement.ownerDocument.documentElement ?
                        viewportRoot :
                        targetElement.ownerDocument;
                }),
                targetElementViewportRoot: elementFindViewportRoot(targetElement),
                targetElement: targetElement
            };
        } else {
            return undefined;
        }

    }, [targetElement]);

    useEffect(() => {

        if (!enabled) {
            return undefined;
        } else if (intersectionObserverElements) {

            const {
                targetElement,
                observerRoot,
                // targetElementViewportRoot
            } = intersectionObserverElements;

            const observer = new IntersectionObserver((entries) => {
                visibleElementsObservableSet.updateBatch(() => {
                    for (const entry of entries) {
                        visibleElementsObservableSet.toggleValue(entry.target, entry.isIntersecting)
                    }
                })
            }, {
                root: observerRoot,
                rootMargin: marginStr,
                threshold: threshold
            });

            let observedElements = [] as Element[];
            const setObservedElements = (newObservedElements: Element[]) => {
                for (const prevObservedElement of observedElements) {
                    observer.unobserve(prevObservedElement);
                }

                for (const observedElement of newObservedElements) {
                    observer.observe(observedElement);
                }

                observedElements = newObservedElements;
            }

            const mutationObserver = new MutationObserver(() => {
                setObservedElements(elementHtmlCollectionToArray(targetElement.children));
            });

            mutationObserver.observe(targetElement, {childList: true, subtree: true});
            setObservedElements(elementHtmlCollectionToArray(targetElement.children));

            // if (areaIntersect(
            //     targetElement.getBoundingClientRect(),
            //     targetElementViewportRoot === null ?
            //         elementGetRootViewport(targetElement) :
            //         targetElementViewportRoot.getBoundingClientRect())
            // ) {
            //     setIsVisible(true);
            // }

            return () => {
                observer.disconnect();
                mutationObserver.disconnect();
            }

        } else {
            return undefined;
        }

    }, [intersectionObserverElements, marginStr, enabled, visibleElementsObservableSet]);

    return useObservable(visibleElementsObservableSet, useCallback(visibleElementsSet => {
        const firstVisibleElement = iterableMin(
            visibleElementsSet.values(),
            visibleElement => elementGetIndexInParent(visibleElement));

        return firstVisibleElement ? elementGetIndexInParent(firstVisibleElement) : undefined;
    }, []))
}


export function useFirstVisibleChildInViewportWithIntent<E extends Element> (
    elementRef: DynamicRef<E>,
    options: {
        frameShrinking?: Edges<number>;
        enabled?: boolean;
        threshold?: number;
        timeout?: number;
    } = {}
) {
    const {
        frameShrinking,
        enabled,
        threshold,
        timeout
    } = options

    const firstVisibleChildInViewport = useFirstVisibleChildInViewport(elementRef, {
        frameShrinking,
        enabled,
        threshold
    })

    const [firstVisibleChildInViewportDebounced] = useDebouncedValue(firstVisibleChildInViewport, timeout);

    return firstVisibleChildInViewportDebounced === firstVisibleChildInViewport ? firstVisibleChildInViewport : undefined;
}